(ns hai.home.desktop.mini-apps.notes
  "My Notes mini-app card: inline-edits the scratchpad note loaded by
   [[hai.notes.stacks]] (auto-saving text, tag/link/contact sections always
   editable), plus an edit button opening the same note in a roomier modal.
   The header's looking glass opens the universal bar; the ＋ archives the
   note and starts a fresh one; the ⋮ holds Delete."
  (:require
   [hai.entity.kebab :as kebab]
   [hai.entity.sections :as sections]
   [hai.notes.card :as note-card]
   [hai.notes.editor :as editor]
   [hai.notes.form :as form]
   [hai.notes.header :as header]
   [hai.notes.mini-app :as notes-mini-app]
   [hai.notes.stacks :as stacks]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.routing :as routing]
   [hai.web-app.universal-bar :as universal-bar]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private expand-icon (icons/mui-icon :outlined :open_in_full))
(def ^:private share-icon (icons/mui-icon :outlined :person_add))

(o/defstyled note-overview :div
  {:padding "18px"
   :height "280px"
   :position "relative"
   :display "flex" :flex-direction "column" :gap "12px"}
  ;; Tucked just inside the text box's top-right corner (the box's border sits at
  ;; the 18px body padding), revealed on hover / keyboard focus.
  [:.note-expand
   {:position "absolute"
    :top "26px" :right "26px"
    :width "30px" :height "30px"
    :border-width "1px" :border-style "solid" :border-color t/--note-sage-35
    :border-radius "8px"
    :background t/--card-surface
    :color t/--note-kraft
    :cursor "pointer"
    :padding "0"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :opacity "0"
    :transition "opacity 0.15s ease, background 0.15s ease"}
   [:svg {:width "15px" :height "15px" :fill "currentColor"}]
   [:&:hover {:background "white"}]
   [:&:focus-visible {:opacity "1"}]]
  [:&:hover [:.note-expand {:opacity "1"}]]
  ;; Paper-airplane share, tucked in the bottom-right corner and always visible
  ;; on the inline card (the full-edit modal has its own share in the footer).
  [:.note-share
   {:position "absolute"
    :bottom "18px" :right "18px"
    :width "30px" :height "30px"
    :border "none" :border-radius "999px"
    :background "transparent"
    :color t/--note-kraft
    :cursor "pointer"
    :padding "0"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :z-index "2"
    :transition "background 0.15s ease"}
   [:svg {:width "15px" :height "15px" :fill "currentColor"}]
   [:&:hover {:background t/--note-kraft-10}]]
  ;; The card is a fixed height. The textarea flexes to fill whatever the
  ;; tags/links rows leave (they keep their natural height), scrolling instead
  ;; of growing the card.
  [:textarea.body
   {:width "100%" :box-sizing "border-box"
    :background "transparent" :outline "none" :resize "none"
    :border "1px solid transparent" :border-radius "8px"
    :padding "10px 12px" :margin "0"
    :font-family "inherit" :font-size "14px" :font-weight "500" :line-height "1.5"
    :color t/--charcoal
    :flex "1 1 auto" :min-height "0"
    :overflow-y "auto"
    :scrollbar-width "thin"
    :scrollbar-color "rgba(74,93,63,0.55) transparent"
    :transition "border-color 0.15s ease"}
   [:&:focus {:border-color "rgba(74,93,63,0.25)"}]
   [:&::placeholder {:color t/--note-sage-55}]]
  ;; Keep the tag/link chips (and their ＋ add controls) clear of the share
  ;; button floating in the bottom-right corner.
  [#{sections/extras-wrap sections/add-section-row}
   {:flex "0 0 auto" :padding-right "40px"}])

(defn- note-body [{:keys [!autosave]}]
  (r/with-let [!modal-open? (r/atom false)
               !share-open? (r/atom false)
               delete!      (fn []
                              (reset! !modal-open? false)
                              (stacks/delete-note! !autosave))]
    (let [loaded?     @(<s [::stacks/loaded?])
          note        @(<s [::stacks/note])
          tags        @(<s [::stacks/tags])
          link-urls   @(<s [::stacks/link-urls])
          contacts    @(<s [::stacks/contacts])
          linked-notes @(<s [::stacks/linked-notes])
          linked-tasks @(<s [::stacks/linked-tasks])
          generation  @(<s [::stacks/generation])
          saving?     @(<s [::stacks/saving?])
          modal-open? @!modal-open?]
      (when loaded?
        [:<>
         [note-overview
          [c/Tooltip {:content "Edit in full screen"}
           [:button.note-expand
            {:type "button"
             :aria-label "Edit in full screen"
             :on-click #(reset! !modal-open? true)}
            [expand-icon]]]
          ;; Remount (re-seeds the uncontrolled textarea) when the modal closes,
          ;; so it picks up edits made there, and when ＋ starts a fresh note
          ;; (::new-note bumps the generation), so the pad clears.
          ^{:key [modal-open? generation]}
          [form/note-form
           {:form-id      (stacks/scratchpad-form-id generation)
            :note         note
            :resume-text? true
            :editing?     true
            :!autosave    !autosave
            :placeholder  "Jot anything down… saves as you type."
            :on-save-text (fn [text] (>e [::stacks/save {:text text}]))}]
          [form/note-extras
           (merge stacks/extras-handlers
                  {:editing?     true
                   :tags         tags
                   :link-urls    link-urls
                   :contacts     contacts
                   :linked-notes linked-notes
                   :linked-tasks linked-tasks
                   :note-uuid    (:note/uuid note)})]
          (when (:note/uuid note)
            [c/Tooltip {:content "Share this note"}
             [:button.note-share
              {:type "button"
               :aria-label "Share this note"
               :on-click #(reset! !share-open? true)}
              [share-icon]]])]
         (when (:note/uuid note)
           [note-card/share-dialog {:note      note
                                    :!open?    !share-open?
                                    :on-closed #(>e [::stacks/refresh])}])
         [editor/dialog
          {:open?    modal-open?
           :on-close #(reset! !modal-open? false)
           :title    "Editing note"}
          (when modal-open?
            [editor/modal-content
             {:title           "Editing note"
              :note            note
              :tags            tags
              :link-urls       link-urls
              :contacts        contacts
              :linked-notes    linked-notes
              :linked-tasks    linked-tasks
              :saving?         saving?
              :form-id         (stacks/scratchpad-form-id generation)
              :on-save-text    (fn [text] (>e [::stacks/save {:text text}]))
              :extras-handlers stacks/extras-handlers
              :on-share-closed #(>e [::stacks/refresh])
              :on-close        #(reset! !modal-open? false)
              :on-delete       delete!}])]]))))

(defn panel []
  (r/with-let [!autosave (atom nil)]
    (let [note @(<s [::stacks/note])]
      [notes-mini-app/card
       [header/bar {:title   "My Notes"
                    :href    (routing/href :notes/view)
                    :target  routing/link-target
                    :dotted? true}
        [c/Tooltip {:content "Search notes"}
         [:button.bar-action-btn
          {:type "button"
           :aria-label "Search notes"
           :on-click #(>e [::universal-bar/open])}
          [icons/outlined-search {:size 18}]]]
        [c/Tooltip {:content "Save this note and get a new one"}
         [:button.bar-action-btn
          {:type "button"
           :aria-label "Save this note and get a new one"
           :on-click (fn []
                       ;; The archive files the live form text itself; drop the
                       ;; pending autosave so the remount flush can't re-save the
                       ;; text onto the fresh pad.
                       (when-let [{:keys [discard!]} @!autosave]
                         (discard!))
                       (>e [::stacks/new-note]))}
          [icons/plus {:size 16}]]]
        (when (:note/uuid note)
          [kebab/menu
           {:on-delete      #(stacks/delete-note! !autosave)
            :delete-label   "Delete note"
            :confirm-prompt "Delete this note?"
            :aria-label     "Note actions"}])]
       [note-body {:!autosave !autosave}]])))
